import React, { useState, useEffect } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import { SectionCard } from "@/components/common/SectionCard";
import CommonTable, { TableColumn, Action } from "@/components/common/CommonTable";
import { TableFilters } from "@/components/common/TableFilters";
import { TableItemIdentity } from "@/components/common/TableItemIdentity";
import { StatusBadge } from "@/components/common/StatusBadge";
import Button from "@/components/common/Button";
import {
    DollarSign,
    ArrowUpRight,
    Plus,
    Search,
    Eye,
    Download
} from "lucide-react";
import { formatRand, showToastNotification, GetFilterUrl, downloadExcel } from "@/utility/helper";
import { callApi, ThrowError } from "@/api/callApi";
import { API_URL, API_ENDPOINT } from "@/api/constant";
import { Ipayout } from '@/interfaces/Payouts/payout.interface';
import { IStore } from '@/interfaces/Store/store.interface';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';
import { MultiSelect } from "@/components/common/MultiSelect";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import { IStorePayoutListResponse } from '@/interfaces/IStoreSettlement/IStoreSettlement.interface';
import { PayoutDetailsModal } from "@/components/common/Model/PayoutDetailsModal";
import { StoreSettlementOrdersModal } from "@/components/common/Model/StoreSettlementOrdersModal";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import DateRangePicker from '@/components/common/DateRange';
import { Range } from 'react-date-range';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export const StoreSettlements = () => {
    const navigate = useRoleNavigate();
    const [payouts, setPayouts] = useState<Ipayout[]>([]);
    const [fetchingPayouts, setFetchingPayouts] = useState(false);
    const [processingPayoutId, setProcessingPayoutId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState<Ipayout | null>(null);
    const [isOrderHistoryModalOpen, setIsOrderHistoryModalOpen] = useState(false);
    const [orderHistoryPayout, setOrderHistoryPayout] = useState<Ipayout | null>(null);
    const { canCreate, canExport } = usePermissions();

    const now = new Date();
    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: startOfMonth(now),
            endDate: endOfMonth(now),
            key: 'selection',
        },
    ]);
    const [loadingExport, setLoadingExport] = useState(false);

    const [stores, setStores] = useState<IStore[]>([]);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo } = useFilter({
        page: 1,
        limit: 10,
        search: '',
        storeId: '',
        status: canCreate(RESOURCES.STORE_SETTLEMENT) ? '' : "approved",
        startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
    });

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const handleExport = async () => {
        const filterParams = {
            search: debouncedFilters.search,
            storeId: debouncedFilters.storeId,
            status: debouncedFilters.status,
            startDate: debouncedFilters.startDate,
            endDate: debouncedFilters.endDate,
            type: 'store',
        };
        const queryParams = GetFilterUrl(filterParams);
        const url = `${API_URL}${API_ENDPOINT.payouts}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(
                url,
                `store_settlements_${filterParams.startDate || 'all'}_to_${filterParams.endDate || 'all'}.xlsx`
            );
            showToastNotification('success', 'Store settlements report exported successfully');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const fetchStores = async () => {
        try {
            const res = await callApi<{ data: { stores: IStore[] } }>(
                `${API_URL}${API_ENDPOINT.stores}/all?page=1&limit=100`,
                { method: 'GET' }
            );
            setStores(res?.data?.stores || []);
        } catch (error) {
            ThrowError(error);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    // Fetch Payouts List
    const fetchPayouts = async () => {
        try {
            setFetchingPayouts(true);
            const queryParams = GetFilterUrl(debouncedFilters);
            const res = await callApi<IStorePayoutListResponse>(
                `${API_URL}${API_ENDPOINT.payouts}/lock/list?${queryParams}`,
                { method: 'GET' }
            );
            setPayouts(res?.data?.payouts || []);
            setPageInfo(res?.data?.pageInfo ?? { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {
            ThrowError(error);
        } finally {
            setFetchingPayouts(false);
        }
    };

    useEffect(() => {
        fetchPayouts();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.storeId, debouncedFilters.startDate, debouncedFilters?.endDate]);

    const handleProceedToPay = async (payoutId: string) => {
        try {
            setProcessingPayoutId(payoutId);
            await new Promise(resolve => setTimeout(resolve, 1000));
            showToastNotification('success', 'Redirecting to payment gateway...');
        } catch (error) {
            ThrowError(error);
        } finally {
            setProcessingPayoutId(null);
        }
    };

    const columns: TableColumn<Ipayout>[] = [
        {
            key: "id",
            header: "Payout ID",
            render: (item) => <span className="font-bold text-foreground">{item.payoutRef}</span>
        },
        {
            key: "store",
            header: "Store",
            render: (item) => item.store ? (
                <div
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate(RoutesLink.storeDetails.replace(':id', item.store!.id))}
                >
                    <TableItemIdentity
                        name={item.store.name}
                        subtitle={item.store.contact}
                        image={item.store.media?.[0]?.url}
                    />
                </div>
            ) : (
                <span className="text-muted-foreground">---</span>
            )
        },
        {
            key: "startDate",
            header: "Start Date",
            render: (item) => <span className="text-muted-foreground">{item.startDate ? new Date(item.startDate).toLocaleDateString() : '---'}</span>
        },
        {
            key: "endDate",
            header: "End Date",
            render: (item) => <span className="text-muted-foreground">{item.endDate ? new Date(item.endDate).toLocaleDateString() : '---'}</span>
        },
        {
            key: "totalOrders",
            header: "Total Orders",
            render: (item) => {
                const count = item.totalOrders ?? item.counts?.totalOrders ?? 0;
                return (
                    <button
                        type="button"
                        onClick={() => {
                            setOrderHistoryPayout(item);
                            setIsOrderHistoryModalOpen(true);
                        }}
                        className="text-sm font-bold text-primary hover:underline hover:text-primary/80 transition-colors flex items-center gap-1 bg-primary/5 px-3 py-1.5 rounded-full cursor-pointer"
                    >
                        {count} orders
                    </button>
                );
            }
        },
        {
            key: "amount",
            header: "Amount",
            render: (item) => <span className="font-bold text-primary">{formatRand(Number(item.amount))}</span>
        },
        {
            key: "status",
            header: "Status",
            render: (item) => <StatusBadge status={item.status} />
        },
    ];

    const actions: Action<Ipayout>[] = [{
        text: 'View Details',
        icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer" />,
        onClick: (record: Ipayout) => {
            setSelectedPayout(record);
            setIsModalOpen(true);
        },
    }];

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Store Settlements"
                    description="Manage created payouts and proceed to payment."
                    actionLabel={canCreate(RESOURCES.STORE_SETTLEMENT) ? "Create Payout" : undefined}
                    actionIcon={canCreate(RESOURCES.STORE_SETTLEMENT) ? <Plus className="w-4 h-4" /> : undefined}
                    onAction={() => navigate(RoutesLink.CreateStoreSettlement)}
                />

                <TableFilters
                    placeholder="Search Payout Reference..."
                    searchTerm={filters.search}
                    onSearchChange={(query) => updateFilter({ search: query, page: 1 })}
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        {canCreate(RESOURCES.STORE_SETTLEMENT) && (
                            <>
                                <div className="w-[220px]">
                                    <MultiSelect
                                        options={stores.map(s => ({ label: s.name, value: s.id }))}
                                        selected={filters.storeId ? [filters.storeId] : []}
                                        onChange={(val) => updateFilter({ storeId: val[0] || "", page: 1 })}
                                        placeholder="Select Store"
                                        singleSelect={true}
                                    />
                                </div>
                                <div className="w-[180px]">
                                    <MultiSelect
                                        options={[
                                            { label: 'All Status', value: '' },
                                            { label: 'Pending', value: 'pending' },
                                            { label: 'Approved', value: 'approved' },
                                        ]}
                                        selected={filters.status ? [filters.status] : []}
                                        onChange={(val) => updateFilter({ status: val[0] || "", page: 1 })}
                                        placeholder="Filter by status"
                                        singleSelect={true}
                                    />
                                </div>
                            </>
                        )}
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.STORE_SETTLEMENT) && (
                            <Button
                                variant="outline"
                                onClick={handleExport}
                                className="h-10 px-4 flex items-center gap-2 rounded-xl"
                                leftIcon={<Download className="w-4 h-4" />}
                                isLoading={loadingExport}
                            >
                                Export
                            </Button>
                        )}
                    </div>
                </TableFilters>

                <SectionCard title="Created Payouts" icon={<DollarSign className="w-5 h-5 text-primary" />}>
                    <CommonTable
                        columns={columns}
                        data={payouts}
                        loading={fetchingPayouts}
                        emptyMessage="No payouts found. Create a payout first."
                        actions={actions}
                    />
                    <Pagination
                        currentPage={filters.page || 1}
                        totalPages={pageInfo.totalPages}
                        totalItems={pageInfo.total}
                        itemsPerPage={pageInfo.limit}
                        onPageChange={handlePageChange}
                    />
                </SectionCard>
            </div>

            <PayoutDetailsModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                payout={selectedPayout}
                onSuccess={fetchPayouts}
                type='store'
            />

            <StoreSettlementOrdersModal
                open={isOrderHistoryModalOpen}
                onOpenChange={setIsOrderHistoryModalOpen}
                storeId={orderHistoryPayout?.store?.id || orderHistoryPayout?.storeId || null}
                startDate={orderHistoryPayout?.startDate || null}
                endDate={orderHistoryPayout?.endDate || null}
            />
        </Layout>
    );
};
